import React, { useState } from 'react';
import { useAuth } from '@/context/auth-context';
import SEO from '@/components/SEO';
import { PricingSection } from '@/components/landing/PricingSection';
import WishlistSection from '@/components/landing/WishlistSection';
import { HomeFAQSection } from '@/components/landing/HomeFAQSection';
import { BackgroundAnimation } from '@/components/background-animation';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { DiscordCTA } from '@/components/DiscordCTA';
import InternalLinksSection from '@/components/InternalLinksSection';
import { ArrowRight, HelpCircle, BookOpen, MessageSquare } from 'lucide-react';

const Plans: React.FC = () => {
  const { user } = useAuth();
  const [selectedWishlistPlan, setSelectedWishlistPlan] = useState<string>('general');

  const handleSelectPlan = (planId: string) => {
    setSelectedWishlistPlan(planId);
  };

  return (
    <div className="min-h-screen bg-zinc-50/50 flex flex-col relative overflow-hidden selection:bg-black selection:text-white">
      <SEO 
        title="Membership Plans"
        description="Choose the right plan for your rhythmic physics creation journey. From hobbyist to pro-creator, we have options to scale your reach."
        canonical="/plans"
      />
      
      <BackgroundAnimation />

      <div className="pt-20">
        <PricingSection 
          user={user} 
          onSelectPlan={handleSelectPlan}
        />
      </div>

      <div className="bg-white">
        <WishlistSection selectedPlan={selectedWishlistPlan} />
      </div>

      <div className="bg-zinc-50">
        <HomeFAQSection />
      </div>

      {/* Help choosing plans */}
      <section className="bg-white py-20 px-6 border-t border-zinc-150 text-center">
        <div className="max-w-4xl mx-auto space-y-12">
          <div className="space-y-4">
            <h2 className="text-3xl font-black font-display text-zinc-950 uppercase">Need Help Choosing?</h2>
            <p className="text-zinc-500 text-sm font-medium font-sans">
              Not sure which plan matches your channel size or production pipeline? Get help directly:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
            <Link to="/help" className="p-6 bg-zinc-50 border border-zinc-200 hover:border-cyan-600 rounded-3xl flex flex-col justify-between group transition-all">
              <div className="space-y-4">
                <div className="w-10 h-10 rounded-2xl bg-cyan-600/10 text-cyan-600 flex items-center justify-center">
                  <HelpCircle className="w-5 h-5" />
                </div>
                <h4 className="font-bold text-zinc-950 uppercase tracking-tight text-sm">Help Center</h4>
                <p className="text-xs text-zinc-500 leading-relaxed font-sans font-medium">
                  Browse pricing-specific documentation, feature limits, and transaction details.
                </p>
              </div>
              <div className="pt-6 text-[10px] font-black uppercase tracking-widest text-cyan-600 flex items-center gap-1">
                Read FAQs <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link to="/tutorials" className="p-6 bg-zinc-50 border border-zinc-200 hover:border-cyan-600 rounded-3xl flex flex-col justify-between group transition-all">
              <div className="space-y-4">
                <div className="w-10 h-10 rounded-2xl bg-cyan-600/10 text-cyan-600 flex items-center justify-center">
                  <BookOpen className="w-5 h-5" />
                </div>
                <h4 className="font-bold text-zinc-950 uppercase tracking-tight text-sm">Creator Academy</h4>
                <p className="text-xs text-zinc-500 leading-relaxed font-sans font-medium">
                  See how parameters behave across subscription levels in our detailed tutorials.
                </p>
              </div>
              <div className="pt-6 text-[10px] font-black uppercase tracking-widest text-cyan-600 flex items-center gap-1">
                Academy <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link to="/contact" className="p-6 bg-zinc-50 border border-zinc-200 hover:border-cyan-600 rounded-3xl flex flex-col justify-between group transition-all">
              <div className="space-y-4">
                <div className="w-10 h-10 rounded-2xl bg-cyan-600/10 text-cyan-600 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5" />
                </div>
                <h4 className="font-bold text-zinc-950 uppercase tracking-tight text-sm">Direct Support</h4>
                <p className="text-xs text-zinc-500 leading-relaxed font-sans font-medium">
                  Have unique needs? Contact our sales team directly for enterprise custom pricing.
                </p>
              </div>
              <div className="pt-6 text-[10px] font-black uppercase tracking-widest text-cyan-600 flex items-center gap-1">
                Contact <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      <DiscordCTA variant="banner" />

      <InternalLinksSection />
    </div>
  );
};

export default Plans;
